// src/pages/legal/KYCPolicy.js
import React from 'react'
import { Box, Container, Typography, Card, CardContent, Divider, Stack } from '@mui/material'
import { useLanguage } from '../../contexts/LanguageContext'

export default function KYCPolicy() {
    const effectiveDate = 'January 5, 2026' // change when needed
    const supportEmail = 'support@mojopayr.com' // change when needed

    const { t, lang } = useLanguage()
    const isFr = lang === 'fr'
    const tt = (key, enFallback, frFallback) => t(key, null, isFr ? frFallback : enFallback)

    const sectionTitleSx = {
        fontWeight: 900,
        color: 'rgba(255,255,255,0.92)',
        mt: 2.4,
        mb: 0.8,
        letterSpacing: 0.2,
    }

    const bodySx = {
        color: 'rgba(255,255,255,0.78)',
        lineHeight: 1.9,
        fontSize: { xs: '0.98rem', md: '1.02rem' },
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
    }

    const liSx = { mb: 0.8 }

    return (
        <Box sx={{ py: { xs: 5, md: 7 } }}>
            <Container maxWidth="md">
                <Typography component="h1" variant="h4" sx={{ fontWeight: 950, color: 'white', mb: 1 }}>
                    {tt('legal.kyc.title', 'KYC Policy', 'Politique KYC')}
                </Typography>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    sx={{ mb: 3, alignItems: { sm: 'center' } }}
                >
                    <Typography sx={{ color: 'rgba(255,255,255,0.72)' }}>
                        {tt(
                            'legal.kyc.subtitle',
                            'Why identity verification is required and what you may need to provide.',
                            "Pourquoi la vérification d’identité est requise et ce que vous devrez peut-être fournir."
                        )}
                    </Typography>

                    <Typography sx={{ color: 'rgba(255,255,255,0.55)' }}>
                        {tt('legal.kyc.effective', 'Effective Date:', 'Date d’effet :')} {effectiveDate}
                    </Typography>
                </Stack>

                <Card
                    sx={{
                        borderRadius: 6,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.10)',
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 2.4, md: 3.2 } }}>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.kyc.intro',
                                'MojoPayr uses Know Your Customer (KYC) checks to help prevent fraud, protect users, and meet legal requirements in the regions where the Service operates.',
                                "MojoPayr utilise des contrôles Know Your Customer (KYC) pour aider à prévenir la fraude, protéger les utilisateurs et respecter les exigences légales dans les régions où le Service est disponible."
                            )}
                        </Typography>

                        <Divider sx={{ my: 2.2, borderColor: 'rgba(255,255,255,0.10)' }} />

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.kyc.s1', '1. When Verification Is Required', '1. Quand la vérification est requise')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.kyc.s1.p1',
                                'We may require identity verification when you create an account, increase limits, add payment methods, send higher-value transfers, or when risk signals require additional review.',
                                "Nous pouvons exiger une vérification d’identité lorsque vous créez un compte, augmentez vos limites, ajoutez des moyens de paiement, effectuez des transferts de montant plus élevé ou lorsque des signaux de risque exigent un examen supplémentaire."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.kyc.s2', '2. Information We May Request', '2. Informations que nous pouvons demander')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.kyc.s2.p1',
                                'Depending on your location and the feature you are using, we may request:',
                                "Selon votre localisation et la fonctionnalité utilisée, nous pouvons demander :"
                            )}
                        </Typography>

                        <Box component="ul" sx={{ mt: 1.2, mb: 0, pl: 2.2, ...bodySx }}>
                            <li style={liSx}>{tt('legal.kyc.s2.li1', 'Full legal name', 'Nom légal complet')}</li>
                            <li style={liSx}>{tt('legal.kyc.s2.li2', 'Date of birth', 'Date de naissance')}</li>
                            <li style={liSx}>{tt('legal.kyc.s2.li3', 'Phone number and email address', 'Numéro de téléphone et adresse e-mail')}</li>
                            <li style={liSx}>{tt('legal.kyc.s2.li4', 'Residential address', 'Adresse de résidence')}</li>
                            <li style={liSx}>
                                {tt(
                                    'legal.kyc.s2.li5',
                                    'Government-issued ID (passport, national ID, driver’s license)',
                                    "Pièce d’identité officielle (passeport, carte d’identité nationale, permis de conduire)"
                                )}
                            </li>
                            <li style={liSx}>
                                {tt(
                                    'legal.kyc.s2.li6',
                                    'Proof of address (utility bill, bank statement, similar document)',
                                    "Justificatif de domicile (facture, relevé bancaire ou document similaire)"
                                )}
                            </li>
                            <li style={liSx}>
                                {tt('legal.kyc.s2.li7', 'Selfie or liveness check (where supported)', 'Selfie ou test de présence (si disponible)')}
                            </li>
                        </Box>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.kyc.s3', '3. Why We Collect This Information', '3. Pourquoi nous collectons ces informations')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.kyc.s3.p1',
                                'We use verification information to confirm identity, reduce fraud, comply with AML laws, and help keep the platform safe.',
                                "Nous utilisons les informations de vérification pour confirmer l’identité, réduire la fraude, respecter les lois AML et aider à maintenir la plateforme en sécurité."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.kyc.s4', '4. Verification Timing', '4. Délais de vérification')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.kyc.s4.p1',
                                'Verification timing can vary. Some checks may complete in minutes, while others may take longer if additional review is required or if documents are unclear.',
                                "Les délais de vérification peuvent varier. Certaines vérifications peuvent se terminer en quelques minutes, tandis que d’autres peuvent prendre plus de temps si un examen supplémentaire est nécessaire ou si les documents sont difficiles à lire."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.kyc.s5', '5. If Verification Fails', '5. En cas d’échec de la vérification')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.kyc.s5.p1',
                                'If verification cannot be completed, we may limit access to certain features, request updated documents, or close the account if required by policy or law.',
                                "Si la vérification ne peut pas être complétée, nous pouvons limiter l’accès à certaines fonctionnalités, demander des documents à jour ou fermer le compte si la politique ou la loi l’exige."
                            )}
                        </Typography>

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.kyc.s6', '6. Keeping Your Information Secure', '6. Protection de vos informations')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt(
                                'legal.kyc.s6.p1',
                                'We apply security controls to help protect KYC data. We retain verification records as required by law and for fraud prevention. For more details, see our Privacy Policy.',
                                "Nous appliquons des contrôles de sécurité pour protéger les données KYC. Nous conservons les dossiers de vérification conformément à la loi et pour la prévention de la fraude. Pour plus de détails, consultez notre Politique de confidentialité."
                            )}
                        </Typography>

                        <Divider sx={{ my: 2.2, borderColor: 'rgba(255,255,255,0.10)' }} />

                        <Typography sx={sectionTitleSx}>
                            {tt('legal.kyc.s7', '7. Contact', '7. Contact')}
                        </Typography>
                        <Typography sx={bodySx}>
                            {tt('legal.kyc.s7.p1', 'If you have questions about KYC, contact us at:', 'Si vous avez des questions sur le KYC, contactez-nous à :')}
                        </Typography>

                        <Typography sx={{ ...bodySx, mt: 1 }}>
                            {tt('legal.kyc.emailLabel', 'Email:', 'E-mail :')}{' '}
                            <Box
                                component="a"
                                href={`mailto:${supportEmail}`}
                                sx={{
                                    color: 'rgba(255,255,255,0.92)',
                                    textDecoration: 'underline',
                                    textUnderlineOffset: '3px',
                                }}
                            >
                                {supportEmail}
                            </Box>
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    )
}

